/**
 * Hiển thị thời gian của nhật ký — port `thoi-gian.ts` native: danh sách chỉ hiện giờ, giờ đầy đủ và giờ tương đối
 * ("5 phút trước") hiện khi rê chuột.
 *
 * Khác native: nhận `locale` (`useT().locale`, `vi-VN` hoặc `en-GB`). Ngày giữ `dd/MM/yyyy` ở cả hai (ADR-UI-0010 —
 * `en-GB` cho đúng dạng ấy), giờ 24 giờ. Mọi hàm nhận chuỗi ISO 8601 có lệch giờ, hiện theo giờ TRÊN MÁY người xem —
 * không tự cắt chuỗi, vì `thoi_gian` mang `+07:00` còn bản ghi khác có thể mang `Z`.
 *
 * Bộ định dạng dựng LÚC GỌI (có bộ nhớ theo locale), không dựng lúc nạp mô-đun: múi giờ mặc định của `Intl` chốt lúc
 * dựng, và test đặt `process.env.TZ` sau khi import.
 */

/** Chỗ không có dữ liệu — một dấu, không phải chuỗi rỗng. Trùng `KHONG_CO` của `lib/dinh-dang.ts`. */
export const TRONG = '—';

const LOCALE_MAC_DINH = 'vi-VN';

const boNho = new Map<string, Intl.DateTimeFormat | Intl.RelativeTimeFormat>();

function boDinhDang(locale: string, loai: 'gio' | 'ngay' | 'gioGiay'): Intl.DateTimeFormat {
  const khoa = `${loai}|${locale}`;
  const daCo = boNho.get(khoa);
  if (daCo) return daCo as Intl.DateTimeFormat;
  const tuyChon: Intl.DateTimeFormatOptions =
    loai === 'ngay'
      ? { day: '2-digit', month: '2-digit', year: 'numeric' }
      : loai === 'gio'
        ? { hour: '2-digit', minute: '2-digit', hour12: false }
        : { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
  const bo = new Intl.DateTimeFormat(locale, tuyChon);
  boNho.set(khoa, bo);
  return bo;
}

function boTuongDoi(locale: string): Intl.RelativeTimeFormat {
  const khoa = `tuongDoi|${locale}`;
  const daCo = boNho.get(khoa);
  if (daCo) return daCo as Intl.RelativeTimeFormat;
  const bo = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  boNho.set(khoa, bo);
  return bo;
}

const BAC: ReadonlyArray<{ don: Intl.RelativeTimeFormatUnit; giay: number }> = [
  { don: 'year', giay: 31_536_000 },
  { don: 'month', giay: 2_592_000 },
  { don: 'day', giay: 86_400 },
  { don: 'hour', giay: 3_600 },
  { don: 'minute', giay: 60 },
];

function moc(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const m = Date.parse(iso);
  return Number.isNaN(m) ? null : m;
}

/** `14:32`. */
export function gio(iso: string | null | undefined, locale: string = LOCALE_MAC_DINH): string {
  const m = moc(iso);
  return m === null ? TRONG : boDinhDang(locale, 'gio').format(m);
}

/** `dd/MM/yyyy` theo cùng bộ định dạng — không tách chuỗi, nên đổi múi giờ đúng. */
function ngay(m: number, locale: string): string {
  const phan = boDinhDang(locale, 'ngay').formatToParts(m);
  const lay = (loai: Intl.DateTimeFormatPartTypes) => phan.find((p) => p.type === loai)?.value ?? '';
  return `${lay('day')}/${lay('month')}/${lay('year')}`;
}

/**
 * `01/10/2026 14:32:05` — ngày trước giờ. Ghép từ hai bộ định dạng chứ không một: `vi-VN` với đủ sáu trường cho ra giờ
 * đứng trước ngày, và trong bảng nhật ký mắt cần mốc ngày để quét.
 */
export function dayDu(iso: string | null | undefined, locale: string = LOCALE_MAC_DINH): string {
  const m = moc(iso);
  return m === null ? TRONG : `${ngay(m, locale)} ${boDinhDang(locale, 'gioGiay').format(m)}`;
}

/**
 * "5 phút trước" / "5 minutes ago". `neo` truyền vào chứ không gọi `Date.now()` bên trong: kiểm được bằng test, và một
 * danh sách đang mở không hiện hai mốc "bây giờ" khác nhau ở hai dòng.
 */
export function tuongDoi(iso: string | null | undefined, neo: number, locale: string = LOCALE_MAC_DINH): string {
  const m = moc(iso);
  if (m === null) return TRONG;
  const lech = Math.round((m - neo) / 1000);
  const doLon = Math.abs(lech);
  const bo = boTuongDoi(locale);
  for (const bac of BAC) {
    if (doLon >= bac.giay) return bo.format(Math.round(lech / bac.giay), bac.don);
  }
  return bo.format(lech, 'second');
}

const MAU_NGAY_LICH = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `'2026-10-13'` → `13/10/2026`. Xếp lại chữ số chứ không đi qua `Date`: đây là ngày lịch (không giờ, không múi giờ),
 * và `new Date('2026-10-13')` là nửa đêm UTC — ở máy lệch giờ âm nó hiện thành ngày 12.
 */
export function ngayLich(ngayLichIso: string | null | undefined): string | null {
  const khop = MAU_NGAY_LICH.exec(ngayLichIso ?? '');
  return khop === null ? null : `${khop[3]}/${khop[2]}/${khop[1]}`;
}

/** Ngày lịch `YYYY-MM-DD` của một mốc ISO, theo giờ trên máy — để so với một ngày lịch khác. */
export function ngayDiaPhuong(iso: string | null | undefined): string | null {
  const m = moc(iso);
  if (m === null) return null;
  const d = new Date(m);
  const hai = (so: number) => String(so).padStart(2, '0');
  return `${d.getFullYear()}-${hai(d.getMonth() + 1)}-${hai(d.getDate())}`;
}

/**
 * Khoảng ngày của một kỳ: `13/10 – 19/10/2026`.
 *
 * `den` là mốc KHÔNG bao gồm (00:00 của ngày đầu kỳ sau), nên ngày cuối hiện ra là ngày chứa mili giây liền trước nó.
 * Trả `null` khi thiếu một trong hai mốc để chỗ gọi tự rơi về cách hiện cũ.
 */
export function khoangKy(
  tu: string | null | undefined,
  den: string | null | undefined,
  locale: string = LOCALE_MAC_DINH,
): string | null {
  const dau = moc(tu);
  const ketThuc = moc(den);
  if (dau === null || ketThuc === null || ketThuc <= dau) return null;
  const cuoi = ketThuc - 1;
  const cungNam = new Date(dau).getFullYear() === new Date(cuoi).getFullYear();
  const dauChu = cungNam ? ngay(dau, locale).slice(0, 5) : ngay(dau, locale);
  return `${dauChu} – ${ngay(cuoi, locale)}`;
}
